import React, { useRef, useEffect, useState } from 'react'
import { getCurrentPlanFeatures } from '../config/planConfig'

const AvatarVideoPlayer = ({ 
  onVideoEnd, 
  autoPlay = true, 
  trigger = 'mount', // 'mount', 'visitor', 'faceAuth'
  className = '' 
}) => {
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [videoError, setVideoError] = useState(false)
  
  const planFeatures = getCurrentPlanFeatures()
  const videoPath = `/assets/videos/${planFeatures.videoFile}`

  useEffect(() => {
    if (autoPlay && videoRef.current) {
      playVideo()
    }
  }, [autoPlay])

  const playVideo = async () => {
    if (!videoRef.current) return

    try {
      setVideoError(false)
      setIsPlaying(true)
      
      // 音声付き自動再生を試行
      await videoRef.current.play()
    } catch (error) {
      console.warn('Video autoplay failed:', error)
      
      // 音声なしで再生を試行
      if (videoRef.current && !videoRef.current.error) {
        videoRef.current.muted = true
        try {
          await videoRef.current.play()
        } catch (mutedError) {
          console.warn('Video play failed completely:', mutedError)
          // 動画再生に失敗した場合、フォールバック表示に切り替え
          setVideoError(true)
          setIsPlaying(false)
          // 2秒後に自動完了
          setTimeout(() => {
            handleVideoEnd()
          }, 2000)
        }
      } else {
        // 動画自体に問題がある場合
        setVideoError(true)
        setIsPlaying(false)
      }
    }
  }

  const handleVideoEnd = () => {
    setIsPlaying(false)
    onVideoEnd && onVideoEnd()
  }

  const handleVideoError = (error) => {
    console.warn(`Video file not found: ${videoPath}`)
    setVideoError(true)
    setIsPlaying(false)
    
    // 動画が見つからない場合、短時間後に完了として処理
    setTimeout(() => {
      handleVideoEnd()
    }, 2000)
  }

  // プランに動画機能がない場合は何も表示しない
  if (!planFeatures.features.videoIntro) {
    return null
  }

  return (
    <div className={`avatar-video-container ${className}`} style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: isPlaying ? 'block' : 'none'
    }}>
      {!videoError ? (
        <video
          ref={videoRef}
          onEnded={handleVideoEnd}
          onError={handleVideoError}
          onLoadStart={() => console.log(`Loading ${planFeatures.name} intro video`)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '12px'
          }}
          playsInline
          preload="auto"
        >
          <source src={videoPath} type="video/mp4" />
          お使いのブラウザは動画再生に対応していません。
        </video>
      ) : (
        // 動画エラー時のフォールバック表示
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: planFeatures.color + '20',
          border: `2px solid ${planFeatures.color}`,
          borderRadius: '12px',
          color: '#666',
          fontSize: '14px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '8px', fontSize: '24px' }}>🎬</div>
            <div>{planFeatures.name}</div>
            <div>イントロ動画準備中</div>
          </div>
        </div>
      )}
      
      {/* 動画制御UI（デバッグ用） */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {planFeatures.name} - {planFeatures.videoDuration}秒
        </div>
      )}
    </div>
  )
}

// プラン別動画トリガーコンポーネント
export const PlanVideoTrigger = ({ trigger, onComplete, children }) => {
  const [showVideo, setShowVideo] = useState(false)
  const [videoCompleted, setVideoCompleted] = useState(false)
  
  useEffect(() => {
    // トリガーに応じて動画再生を開始
    if (trigger === 'visitor' || trigger === 'faceAuth' || trigger === 'mount') {
      setShowVideo(true)
      setVideoCompleted(false)
    }
  }, [trigger])

  const handleVideoEnd = () => {
    setShowVideo(false)
    setVideoCompleted(true)
    onComplete && onComplete()
  }

  return (
    <>
      {showVideo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            width: '80%',
            maxWidth: '600px',
            height: '60%',
            maxHeight: '400px'
          }}>
            <AvatarVideoPlayer
              onVideoEnd={handleVideoEnd}
              autoPlay={true}
              trigger={trigger}
            />
          </div>
        </div>
      )}
      {/* 動画完了後または動画なしの場合に子コンポーネントを表示 */}
      {(videoCompleted || !showVideo) && children}
    </>
  )
}

export default AvatarVideoPlayer